import { Controller } from "@hotwired/stimulus"

// Manages the Edit Roles drawer interactions:
// - Adding/removing role cards with scope chips
// - Autocomplete employer picker (type -> filter -> select -> chip -> refocus)
// - Previewing role capabilities when selecting from dropdown
// - Building hidden form fields for save
export default class extends Controller {
  static targets = [
    "rolesContainer", "roleCard", "emptyMessage", "scopeChips",
    "addRoleButton", "addRoleForm", "roleSelect",
    "scopeTypeSelect", "employerPicker", "employerInput",
    "employerResults", "employerChips", "employerAutocompleteWrapper",
    "employerData",
    "rolePreview", "roleDescription", "roleCapabilities",
    "addBtn", "saveForm", "hiddenFields"
  ]
  static values = { saveUrl: String, userId: Number, partnerId: Number }

  connect() {
    this._allEmployers = []
    if (this.hasEmployerDataTarget) {
      try {
        this._allEmployers = JSON.parse(this.employerDataTarget.textContent)
      } catch (e) {
        this._allEmployers = []
      }
    }
    this._selectedEmployers = []
    this._highlightedIndex = -1
  }

  // --- Role card management ---

  removeRole(event) {
    event.preventDefault()
    const card = event.currentTarget.closest("[data-edit-roles-target='roleCard']")
    if (card) {
      card.remove()
    }
    if (this.roleCardTargets.length === 0 && this.hasEmptyMessageTarget) {
      this.emptyMessageTarget.classList.remove("hidden")
    } else if (this.roleCardTargets.length === 0) {
      const msg = document.createElement("div")
      msg.dataset.editRolesTarget = "emptyMessage"
      msg.className = "text-[13px] text-support-dark-400 py-3"
      msg.textContent = "No roles assigned"
      this.rolesContainerTarget.prepend(msg)
    }
  }

  // Called after a scope chip is removed (via remove-element controller).
  // If the role card has no remaining scope chips, remove the entire card.
  onScopeChipRemoved() {
    setTimeout(() => {
      this.roleCardTargets.forEach(card => {
        const chipsContainer = card.querySelector("[data-edit-roles-target='scopeChips']")
        if (chipsContainer && chipsContainer.children.length === 0) {
          card.remove()
        }
      })
      if (this.roleCardTargets.length === 0) {
        if (this.hasEmptyMessageTarget) {
          this.emptyMessageTarget.classList.remove("hidden")
        } else {
          const msg = document.createElement("div")
          msg.dataset.editRolesTarget = "emptyMessage"
          msg.className = "text-[13px] text-support-dark-400 py-3"
          msg.textContent = "No roles assigned"
          this.rolesContainerTarget.prepend(msg)
        }
      }
    }, 0)
  }

  showAddRole() {
    this.addRoleButtonTarget.classList.add("hidden")
    this.addRoleFormTarget.classList.remove("hidden")
    this.roleSelectTarget.value = ""
    this.rolePreviewTarget.classList.add("hidden")
    this.resetEmployerPicker()
    this.updateAddButton()
  }

  cancelAddRole() {
    this.addRoleFormTarget.classList.add("hidden")
    this.addRoleButtonTarget.classList.remove("hidden")
    this.roleSelectTarget.value = ""
    this.rolePreviewTarget.classList.add("hidden")
    this.resetEmployerPicker()
  }

  // --- Scope type ---

  onScopeTypeChange() {
    const scopeType = this.scopeTypeSelectTarget.value
    if (scopeType === "Employer") {
      this.employerPickerTarget.classList.remove("hidden")
      if (this._allEmployers.length === 0 && this.hasEmployerDataTarget) {
        try {
          this._allEmployers = JSON.parse(this.employerDataTarget.textContent)
        } catch (e) {
          this._allEmployers = []
        }
      }
    } else {
      this.employerPickerTarget.classList.add("hidden")
    }
    this.updateAddButton()
  }

  // --- Employer autocomplete ---

  filterEmployers() {
    const query = this.employerInputTarget.value.trim().toLowerCase()
    const selectedIds = this._selectedEmployers.map(e => String(e.id))

    const matches = this._allEmployers.filter(emp =>
      !selectedIds.includes(String(emp.id)) &&
      (query === "" || emp.name.toLowerCase().includes(query))
    )

    const results = this.employerResultsTarget
    results.innerHTML = ""
    this._highlightedIndex = -1

    if (matches.length === 0 || query === "") {
      results.classList.add("hidden")
      return
    }

    matches.forEach((emp, idx) => {
      const li = document.createElement("li")
      li.className = "px-3 py-2 cursor-pointer text-[13px] text-support-dark-800 hover:bg-support-light-200"
      li.textContent = emp.name
      li.dataset.employerId = emp.id
      li.dataset.employerName = emp.name
      li.dataset.index = idx
      li.addEventListener("click", () => this.selectEmployer(emp))
      results.appendChild(li)
    })

    results.classList.remove("hidden")

    this._removeEmployerResultsHandler()
    this._employerResultsClickHandler = (event) => {
      if (!this.employerAutocompleteWrapperTarget.contains(event.target)) {
        this.employerResultsTarget.classList.add("hidden")
        this._removeEmployerResultsHandler()
      }
    }
    document.addEventListener("click", this._employerResultsClickHandler)
  }

  onEmployerInputKeydown(event) {
    const results = this.employerResultsTarget
    const items = results.querySelectorAll("li")

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this._highlightedIndex = Math.min(this._highlightedIndex + 1, items.length - 1)
      this._updateHighlight(items)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this._highlightedIndex = Math.max(this._highlightedIndex - 1, 0)
      this._updateHighlight(items)
    } else if (event.key === "Enter") {
      event.preventDefault()
      if (this._highlightedIndex >= 0 && items[this._highlightedIndex]) {
        const li = items[this._highlightedIndex]
        this.selectEmployer({
          id: li.dataset.employerId,
          name: li.dataset.employerName
        })
      }
    } else if (event.key === "Escape") {
      results.classList.add("hidden")
      this._removeEmployerResultsHandler()
    }
  }

  _updateHighlight(items) {
    items.forEach((li, idx) => {
      if (idx === this._highlightedIndex) {
        li.classList.add("bg-support-light-200")
        li.scrollIntoView({ block: "nearest" })
      } else {
        li.classList.remove("bg-support-light-200")
      }
    })
  }

  selectEmployer(emp) {
    if (this._selectedEmployers.some(e => String(e.id) === String(emp.id))) return

    this._selectedEmployers.push({ id: emp.id, name: emp.name })
    this.renderEmployerChips()
    this.updateAddButton()

    // Clear input, close results, refocus for next entry
    this.employerInputTarget.value = ""
    this.employerResultsTarget.classList.add("hidden")
    this._removeEmployerResultsHandler()
    this.employerInputTarget.focus()
  }

  renderEmployerChips() {
    const container = this.employerChipsTarget
    container.innerHTML = ""
    this._selectedEmployers.forEach(({ id, name }) => {
      const chipId = `employer-chip-${id}`
      const wrapper = document.createElement("div")
      wrapper.id = chipId
      wrapper.dataset.employerId = id

      // Match the shared/_chip partial: chip styles, label, dismiss X with remove-element
      wrapper.innerHTML = `
        <div class="px-4 py-2 rounded-lg justify-end items-center gap-4 inline-flex border bg-support-dark-200 text-support-dark-700 border-support-dark-250 hover:bg-support-dark-250 hover:text-support-dark-800 hover:border-support-dark-300 whitespace-nowrap">
          <div class="text-xs font-semibold font-['Neue Haas Grotesk Text Pro'] leading-[18px]">${this.escapeHtml(name)}</div>
          <div class="cursor-pointer"
               role="button"
               aria-label="Remove ${this.escapeHtml(name)}"
               data-controller="remove-element"
               data-element-id="${chipId}">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 256 256"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path></svg>
          </div>
        </div>
      `

      // When chip is removed by remove-element, update _selectedEmployers
      const observer = new MutationObserver(() => {
        if (!document.getElementById(chipId)) {
          this._selectedEmployers = this._selectedEmployers.filter(e => String(e.id) !== String(id))
          this.updateAddButton()
          observer.disconnect()
        }
      })
      observer.observe(container, { childList: true })

      container.appendChild(wrapper)
    })
  }

  _removeEmployerResultsHandler() {
    if (this._employerResultsClickHandler) {
      document.removeEventListener("click", this._employerResultsClickHandler)
      this._employerResultsClickHandler = null
    }
  }

  selectedEmployers() {
    return this._selectedEmployers || []
  }

  resetEmployerPicker() {
    this.scopeTypeSelectTarget.value = "Partner"
    this.employerPickerTarget.classList.add("hidden")
    this._selectedEmployers = []
    if (this.hasEmployerInputTarget) {
      this.employerInputTarget.value = ""
    }
    if (this.hasEmployerResultsTarget) {
      this.employerResultsTarget.classList.add("hidden")
      this.employerResultsTarget.innerHTML = ""
    }
    if (this.hasEmployerChipsTarget) {
      this.employerChipsTarget.innerHTML = ""
    }
    this._removeEmployerResultsHandler()
  }

  previewRole() {
    const select = this.roleSelectTarget
    const option = select.options[select.selectedIndex]

    if (!select.value) {
      this.rolePreviewTarget.classList.add("hidden")
      this.updateAddButton()
      return
    }

    const description = option.dataset.description || ""
    const checkColor = option.dataset.checkColor || "text-support-dark-500"

    this.roleDescriptionTarget.textContent = description

    // Parse and render capabilities
    let permissions = {}
    try {
      permissions = JSON.parse(option.dataset.permissions || "{}")
    } catch (e) {
      permissions = {}
    }

    const capContainer = this.roleCapabilitiesTarget
    capContainer.innerHTML = ""

    Object.entries(permissions).forEach(([category, perms], idx) => {
      const wrapper = document.createElement("div")
      wrapper.className = idx < Object.keys(permissions).length - 1 ? "mb-2" : ""

      const catLabel = document.createElement("div")
      catLabel.className = "text-[9px] font-bold text-support-dark-600 uppercase tracking-wider mb-1"
      catLabel.textContent = category
      wrapper.appendChild(catLabel)

      perms.forEach(perm => {
        const row = document.createElement("div")
        row.className = "flex items-start gap-1.5 mb-0.5"
        row.innerHTML = `
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" class="flex-shrink-0 mt-0.5 ${checkColor}">
            <circle cx="7" cy="7" r="7" fill="currentColor" opacity="0.12" />
            <path d="M4 7.2L6 9.2L10 5.2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <span class="text-[11px] text-support-dark-600 leading-snug">${this.escapeHtml(perm.description || perm.name || String(perm))}</span>
        `
        wrapper.appendChild(row)
      })

      capContainer.appendChild(wrapper)
    })

    this.rolePreviewTarget.classList.remove("hidden")
    this.updateAddButton()
  }

  updateAddButton() {
    const hasRole = this.roleSelectTarget.value !== ""
    const scopeType = this.scopeTypeSelectTarget.value
    const hasScope = scopeType === "Partner" || this.selectedEmployers().length > 0
    const btn = this.addBtnTarget
    if (hasRole && hasScope) {
      btn.disabled = false
      btn.className = "px-[18px] py-2 rounded-md border-none bg-core-purple-500 text-white text-[13px] font-semibold cursor-pointer"
    } else {
      btn.disabled = true
      btn.className = "px-[18px] py-2 rounded-md border-none bg-support-dark-400 text-white text-[13px] font-semibold cursor-default"
    }
  }

  addRole() {
    const select = this.roleSelectTarget
    const option = select.options[select.selectedIndex]
    if (!select.value) return

    const roleId = select.value
    const roleName = option.dataset.roleName || option.textContent.trim()
    const pillColors = option.dataset.pillColors || ""
    const checkColor = option.dataset.checkColor || "text-support-dark-500"
    const description = option.dataset.description || ""

    // Gather scope entries: either Partner or multiple employers
    const scopeType = this.scopeTypeSelectTarget.value
    let scopes = []
    if (scopeType === "Partner") {
      const partnerOption = this.scopeTypeSelectTarget.options[0]
      scopes = [{ type: "Partner", id: partnerOption.dataset.scopeId || "", label: "Partner (all employers)" }]
    } else {
      scopes = this.selectedEmployers().map(emp => ({
        type: "Employer", id: emp.id, label: emp.name
      }))
    }
    if (scopes.length === 0) return

    // Parse permissions for the capability display
    let permissions = {}
    try {
      permissions = JSON.parse(option.dataset.permissions || "{}")
    } catch (e) {
      permissions = {}
    }

    const capabilityHtml = this.buildCapabilityHtml(permissions, checkColor)

    // Build pill HTML
    const pillClasses = `h-6 px-2 py-[3px] rounded-lg justify-end items-center gap-[3px] inline-flex border ${pillColors} whitespace-nowrap`
    const pillHtml = `<span class="${pillClasses}"><span class="text-xs font-semibold font-['Neue Haas Grotesk Text Pro'] leading-[18px]">${this.escapeHtml(roleName)}</span></span>`

    // Build scope chips HTML
    const scopeChipsHtml = scopes.map(scope => {
      const chipId = `scope-chip-${roleId}-${scope.type}-${scope.id}`
      return `
        <div id="${chipId}" data-scope-type="${this.escapeHtml(scope.type)}" data-scope-id="${this.escapeHtml(scope.id)}">
          <div class="px-4 py-2 rounded-lg justify-end items-center gap-4 inline-flex border bg-support-dark-200 text-support-dark-700 border-support-dark-250 hover:bg-support-dark-250 hover:text-support-dark-800 hover:border-support-dark-300 whitespace-nowrap">
            <div class="text-xs font-semibold font-['Neue Haas Grotesk Text Pro'] leading-[18px]">${this.escapeHtml(scope.label)}</div>
            <div class="cursor-pointer"
                 role="button"
                 aria-label="Remove ${this.escapeHtml(scope.label)}"
                 data-controller="remove-element"
                 data-element-id="${chipId}"
                 data-action="click->edit-roles#onScopeChipRemoved">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 256 256"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path></svg>
            </div>
          </div>
        </div>
      `
    }).join("")

    // Create ONE card per role with all scope chips inside
    const card = document.createElement("div")
    card.className = "py-4 border-b border-support-light-200"
    card.dataset.editRolesTarget = "roleCard"
    card.dataset.roleId = roleId
    card.innerHTML = `
      <div class="flex justify-between items-start">
        <div class="flex-1">
          ${pillHtml}
          <div class="text-[13px] text-support-dark-600 mt-1.5 leading-relaxed">${this.escapeHtml(description)}</div>
        </div>
        <a href="#"
           data-action="click->edit-roles#removeRole"
           class="text-link text-alert-red-500 text-xs font-semibold flex-shrink-0">
          Remove
        </a>
      </div>
      <div class="flex flex-wrap gap-2 mt-2" data-edit-roles-target="scopeChips">
        ${scopeChipsHtml}
      </div>
      ${capabilityHtml}
    `
    this.rolesContainerTarget.appendChild(card)

    // Hide empty message if present
    if (this.hasEmptyMessageTarget) {
      this.emptyMessageTarget.classList.add("hidden")
    }

    this.cancelAddRole()
  }

  buildCapabilityHtml(permissions, checkColor) {
    const entries = Object.entries(permissions)
    const totalCount = entries.reduce((sum, [, perms]) => sum + perms.length, 0)
    if (totalCount === 0) return ""

    let innerHtml = ""
    entries.forEach(([category, perms], idx) => {
      innerHtml += `<div class="${idx < entries.length - 1 ? 'mb-3.5' : ''}">`
      innerHtml += `<div class="text-[10px] font-bold text-support-dark-600 uppercase tracking-wider mb-1.5">${this.escapeHtml(category)}</div>`
      innerHtml += `<div class="flex flex-col gap-1.5">`
      perms.forEach(perm => {
        const permDesc = perm.description || perm.name || String(perm)
        innerHtml += `
          <div class="flex items-start gap-1.5">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" class="flex-shrink-0 mt-0.5 ${checkColor}">
              <circle cx="7" cy="7" r="7" fill="currentColor" opacity="0.12" />
              <path d="M4 7.2L6 9.2L10 5.2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span class="text-xs text-support-dark-600 leading-snug">${this.escapeHtml(permDesc)}</span>
          </div>`
      })
      innerHtml += `</div></div>`
    })

    return `
      <div class="mt-2.5" data-controller="capabilities-toggle">
        <a href="#"
           data-action="click->capabilities-toggle#toggle"
           class="text-link text-xs font-semibold text-core-purple-500">
          <span data-capabilities-toggle-target="label">View ${totalCount} ${totalCount === 1 ? 'capability' : 'capabilities'}</span>
        </a>
        <div data-capabilities-toggle-target="content" class="hidden mt-2.5 p-3.5 bg-support-light-200 rounded-lg border border-support-light-300">
          ${innerHtml}
        </div>
      </div>`
  }

  prepareSave(event) {
    // Build hidden fields from current role cards + scope chips
    const fields = this.hiddenFieldsTarget
    fields.innerHTML = ""

    this.roleCardTargets.forEach(card => {
      const roleId = card.dataset.roleId
      const chipsContainer = card.querySelector("[data-edit-roles-target='scopeChips']")

      if (chipsContainer) {
        // One hidden field group per scope chip in the card
        const chips = chipsContainer.querySelectorAll("[data-scope-type]")
        chips.forEach(chip => {
          const scopeType = chip.dataset.scopeType
          const scopeId = chip.dataset.scopeId
          const scimUserProvisioningId = chip.dataset.scimUserProvisioningId
          fields.insertAdjacentHTML('beforeend',
            `<input type="hidden" name="roles[][role_id]" value="${this.escapeHtml(roleId)}">` +
            `<input type="hidden" name="roles[][scope_type]" value="${this.escapeHtml(scopeType)}">` +
            `<input type="hidden" name="roles[][scope_id]" value="${this.escapeHtml(scopeId)}">` +
            (scimUserProvisioningId
              ? `<input type="hidden" name="roles[][scim_user_provisioning_id]" value="${this.escapeHtml(scimUserProvisioningId)}">`
              : "")
          )
        })
      } else {
        // Fallback: card-level scope data (for existing cards from server)
        const scopeType = card.dataset.scopeType || ""
        const scopeId = card.dataset.scopeId || ""
        fields.insertAdjacentHTML('beforeend',
          `<input type="hidden" name="roles[][role_id]" value="${this.escapeHtml(roleId)}">` +
          `<input type="hidden" name="roles[][scope_type]" value="${this.escapeHtml(scopeType)}">` +
          `<input type="hidden" name="roles[][scope_id]" value="${this.escapeHtml(scopeId)}">`
        )
      }
    })
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
